import type { TvShowEpisode } from '@setplex/tria-api'
import type { Store } from 'effector'
import type { TvShowEpisodePayment } from '~/features/payment/interfaces'
import type { ExternalPaymentParams, ISelectedPaymentContent } from '../index.h'

import { api } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
  split,
} from 'effector'
import { model as router, model as routerModel } from '~/core/router'
import { model as catchupsModel } from '~/entities/catchups'
import { playerShowModel } from '~/entities/player'
import { model as redirectUrlModel } from '~/entities/redirect-url'
import { model as sessionModel } from '~/entities/session'
import {
  $pinLockPopup,
  closePinLockPopup,
  emptyPinLockState,
  openPinLockPopup,
  pinlockProfilesFeatureModel,
} from '~/features/pin-lock'
import { profilesFeaturesModel } from '~/features/profiles'
import { model as config, remote } from '~/shared/config'
import { aye, nay, noop } from '~/shared/helpers'
import {
  EXTERNAL_PAYMENT_PINLOCK_ID,
  MediaTypeInternal,
  MediaTypes,
  MediaTypesExtended,
} from '../index.h'
import { isFree, isPaid } from '../lib'
import { formatTvShowEpisodePayment } from './helpers'

export const tryClosePaymentPopup = createEvent() // checks if it really opened before close

export const closePaymentPopup = createEvent()

export const $isAnyPaymentPopupOpened = createStore<boolean>(false)

sample({
  clock: tryClosePaymentPopup,
  source: $isAnyPaymentPopupOpened,
  filter: (isAnyPaymentPopupOpened) => aye(isAnyPaymentPopupOpened),
  target: closePaymentPopup,
})

sample({
  clock: $isAnyPaymentPopupOpened,
  target: closePinLockPopup,
})

// close payment popup on location change
sample({
  clock: router.$location,
  target: tryClosePaymentPopup,
})

export const selectForExternalPayment = createEvent<ExternalPaymentParams>()

export const $selectedContent = createStore<ISelectedPaymentContent>(null).on(
  selectForExternalPayment,
  (_, selectedItem) => selectedItem
)

export const redirectToPlayerOrRefresh =
  createEvent<Pick<ExternalPaymentParams, 'mediaType' | 'params'>>()

const $remoteShoppingCartUrl = config.get(remote.tria_shoppingCartUrl)

export const $externalPaymentBaseUrl = combine(
  $remoteShoppingCartUrl,
  sessionModel.$session,
  (remoteShoppingCartUrl, session) =>
    session?.shoppingCartUrl || remoteShoppingCartUrl
)

export const $externalPaymentEnabled = $externalPaymentBaseUrl.map(Boolean)

export const openExternalPaymentPopup = createEvent()

export const closeExternalPaymentPopup = createEvent()

// Intermediate event for pinlock integration
export const handleOpenExternalPaymentPopup = createEvent()

// store-function (pipe) for skin, to check if entity is free
export const $isFree = combine(() => isFree)

// store-function (pipe) for skin, to check if entity is paid
export const $isPaid = combine(() => isPaid)

// If profile is NOT regular (kids profile), open pinlock popup first
sample({
  clock: handleOpenExternalPaymentPopup,
  source: {
    isActiveProfileRegular: profilesFeaturesModel.$isActiveProfileRegular,
    pinLockPopup: $pinLockPopup,
  },
  filter: ({ isActiveProfileRegular, pinLockPopup }) =>
    nay(isActiveProfileRegular) && nay(pinLockPopup.id),
  fn: () => ({
    ...emptyPinLockState,
    profileUnlock: true,
    id: EXTERNAL_PAYMENT_PINLOCK_ID,
  }),
  target: openPinLockPopup,
})

// When pinlock is unlocked with EXTERNAL_PAYMENT_PINLOCK_ID, proceed to open payment popup
sample({
  clock: pinlockProfilesFeatureModel.setUnlocked,
  filter: (unlockedId) => unlockedId === EXTERNAL_PAYMENT_PINLOCK_ID,
  target: openExternalPaymentPopup,
})

// If profile IS regular, proceed directly to open payment popup
sample({
  clock: handleOpenExternalPaymentPopup,
  source: profilesFeaturesModel.$isActiveProfileRegular,
  filter: (isActiveProfileRegular) => aye(isActiveProfileRegular),
  target: openExternalPaymentPopup,
})

const isTvChannelPurchasedFx = createEffect(async ({ id }: { id: number }) => {
  const channels = await api.tv.getTvChannelsByIdFx({ ids: [id] })
  return channels?.[0]?.purchaseInfo != null
})

const isTvChannelBundlePurchasedFx = createEffect(
  async ({ id }: { id: number }) => {
    const bundle = await api.tvBundles.getTvBundleInfoFx({ bundleId: id })
    return bundle?.purchaseInfo != null
  }
)

const isVodPurchasedFx = createEffect(async ({ id }: { id: number }) => {
  const vod = await api.vods.getVodFx({ vodId: id })
  return vod?.purchaseInfo != null
})

const isPartnerProductPurchasedFx = createEffect(
  async ({ id }: { id: number }) => {
    const partnerProduct = await api.partnerProgram.getPartnerProductDetailsFx({
      partnerProductId: id,
    })
    return partnerProduct?.purchaseInfo != null
  }
)

const isLiveEventPurchasedFx = createEffect(async ({ id }: { id: number }) => {
  const liveEvent = await api.liveEvents.getLiveEventByIdFx({
    eventId: id,
  })
  return liveEvent?.purchaseInfo != null
})

const isVodBundlePurchasedFx = createEffect(async ({ id }: { id: number }) => {
  const bundle = await api.vodBundles.getVodBundleInfoFx({ bundleId: id })
  return bundle?.purchaseInfo != null
})

const isTvShowPurchasedFx = createEffect(async ({ id }: { id: number }) => {
  const shows = await api.tvshows.getTvShowBaseFx({ tvShowId: id })

  return shows?.[0]?.purchaseInfo != null
})

const isTvShowSeasonPurchasedFx = createEffect(
  async ({ id, tvShowId }: { id: number; tvShowId: number }) => {
    const season = await api.tvshows.getTvShowSeasonByIdBaseFx({
      tvShowId,
      seasonId: id,
    })
    return season?.purchaseInfo != null
  }
)

const isTvShowEpisodePurchasedFx = createEffect(
  async (params: { id: number; seasonId?: number; tvShowId: number }) => {
    let episode: TvShowEpisode

    if (params.seasonId) {
      episode = await api.tvshows.getTvShowEpisodeFx({
        tvShowId: params.tvShowId,
        seasonId: params.seasonId,
        episodeId: params.id,
      })
    } else {
      episode = await api.tvshows.getTvShowEpisodeWithoutSeasonFx({
        tvShowId: params.tvShowId,
        episodeId: params.id,
      })
    }

    return episode?.purchaseInfo != null
  }
)

const isTvShowBundlePurchasedFx = createEffect(
  async ({ id }: { id: number }) => {
    const bundle = await api.showBundles.getShowBundleInfoFx({ bundleId: id })
    return bundle?.purchaseInfo != null
  }
)

// do not delete

export const isPurchasedRawFx = createEffect(
  async ({
    id,
    seasonId,
    tvShowId,
    mediaType,
  }: ExternalPaymentParams): Promise<boolean> => {
    try {
      switch (mediaType) {
        case MediaTypes.TV_CHANNEL:
        case MediaTypesExtended.TV_CHANNEL_IN_BUNDLE:
        case MediaTypesExtended.TV_CHANNEL_CATCHUP:
          return await isTvChannelPurchasedFx({ id })
        case MediaTypesExtended.TV_CHANNEL_BUNDLE:
          return await isTvChannelBundlePurchasedFx({ id })
        case MediaTypes.VOD:
        case MediaTypesExtended.VOD_IN_BUNDLE:
          return await isVodPurchasedFx({ id })
        case MediaTypesExtended.VOD_BUNDLE:
          return await isVodBundlePurchasedFx({ id })
        case MediaTypes.TV_SHOW:
        case MediaTypesExtended.TV_SHOW_IN_BUNDLE:
          return await isTvShowPurchasedFx({ id })
        case MediaTypesExtended.TV_SHOW_SEASON:
        case MediaTypesExtended.TV_SHOW_SEASON_IN_BUNDLE:
          return (
            tvShowId != null &&
            (await isTvShowSeasonPurchasedFx({ id, tvShowId }))
          )
        case MediaTypesExtended.TV_SHOW_EPISODE:
        case MediaTypesExtended.TV_SHOW_EPISODE_IN_BUNDLE:
          return (
            tvShowId != null &&
            (await isTvShowEpisodePurchasedFx({
              id,
              seasonId: seasonId,
              tvShowId: tvShowId,
            }))
          )
        case MediaTypesExtended.TV_SHOW_BUNDLE:
          return await isTvShowBundlePurchasedFx({ id })
        case MediaTypes.PARTNER_PRODUCT:
          return await isPartnerProductPurchasedFx({ id })
        case MediaTypes.LIVE_EVENT:
          return await isLiveEventPurchasedFx({ id })
      }
    } catch (ignore) {
      // ignore
    }
    return false
  }
)

const externalIsPurchasedFx = attach({ effect: isPurchasedRawFx })

sample({
  clock: selectForExternalPayment,
  target: externalIsPurchasedFx,
})

sample({
  clock: externalIsPurchasedFx.done,
  filter: ({ result: isPurchased }) => isPurchased,
  fn: ({ params }) => ({
    mediaType: params.shouldRefresh
      ? MediaTypeInternal.__REFRESH__
      : params.mediaType,
    params: params.params,
  }),
  target: redirectToPlayerOrRefresh,
})

split({
  source: redirectToPlayerOrRefresh,
  match: ({ mediaType }) =>
    mediaType as
      | 'TV_CHANNEL'
      | 'TV_SHOW_EPISODE'
      | 'TV_CHANNEL_CATCHUP'
      | '__REFRESH__',
  cases: {
    TV_CHANNEL: redirectUrlModel.redirectToTvPlayer.prepend(noop),
    TV_SHOW_EPISODE: redirectUrlModel.redirectToShowPlayer.prepend(noop),
    TV_CHANNEL_CATCHUP: catchupsModel.navigateCatchupPlayer.prepend<any>(
      ({ params }) => params
    ),
    __REFRESH__: router.refresh,
  },
})

sample({
  clock: externalIsPurchasedFx.done,
  filter: ({ result: isPurchased }) => !isPurchased,
  target: handleOpenExternalPaymentPopup,
})

sample({
  clock: closePaymentPopup,
  target: closeExternalPaymentPopup,
})

export const $tvShowEpisodePayment: Store<TvShowEpisodePayment | null> =
  combine(
    playerShowModel.$tvShowCurrentEpisode,
    routerModel.$query,
    api.tvshows.$seriesPlaylist,
    (episode, query, seriesPlaylist) => {
      if (!episode) return null

      return formatTvShowEpisodePayment(episode, query, seriesPlaylist)
    }
  )
